/**
 * symbols — extract every visual asset *symbol* referenced by a `.ls` source.
 *
 * The asset-binding workspace (§ asset mapping) needs to know, ahead of any
 * compile, which backgrounds / character looks / CGs a script names so it can
 * show coverage and let the author bind each symbol to an `assetId`. This is a
 * pure, tolerant scan layered on the display parser (`parse-blocks.ts`): it
 * never throws and tolerates partial / malformed input, emitting only the
 * symbols it can confidently recognise.
 *
 * Symbol semantics (mirroring LS-SPEC §4.2):
 *  - backgrounds: the `<name>` in `@bg set <name> [transition]`.
 *  - characters:  the lowercase char id used by `@<char> <pose>` directives;
 *    each `pose` is one "look". Dialogue pose-sugar `CHAR [look]:` contributes
 *    a look too — the SCREAMING speaker is lower-cased to the canonical id.
 *    `@<char> bubble <type>` is NOT a look (`bubble` is a reserved word and is
 *    never a drawable pose asset), so it only registers the character.
 *  - cg:          the `<name>` in `@cg <name> "<content>"`.
 *  - music:       the `<track>` in `@music <track>` / `&music <track>`. The
 *    `@music stop` form controls playback and names no asset, so it is skipped.
 *  - sfx:         the `<name>` in `@sfx <name>` / `&sfx <name>`.
 *
 * Audio (music / sfx) can't be AI-generated, so these symbols exist purely so
 * the author can UPLOAD and bind an audio file to each one.
 *
 * De-duplication preserves first-seen order at every level (character order,
 * per-character look order, background order, cg order, music order, sfx order).
 */

import { parseLsBlocks } from "../ls/parse-blocks.js";

/** A character and the ordered, de-duplicated set of looks (poses) it uses. */
export interface ParsedCharacter {
  /** Canonical lowercase character id (`rin`, `malia`, …). */
  readonly id: string;
  /** Ordered, de-duplicated pose/look symbols referenced for this character. */
  readonly looks: string[];
}

/** Every asset symbol a `.ls` source references, grouped by kind. */
export interface ParsedSymbols {
  /** Ordered, de-duplicated background symbols. */
  readonly backgrounds: string[];
  /** Characters in first-seen order, each with its ordered looks. */
  readonly characters: ParsedCharacter[];
  /** Ordered, de-duplicated CG symbols. */
  readonly cg: string[];
  /** Ordered, de-duplicated music-track symbols (`@music <track>`). */
  readonly music: string[];
  /** Ordered, de-duplicated SFX symbols (`@sfx <name>`). */
  readonly sfx: string[];
}

/**
 * Scan `.ls` source for every asset symbol it references.
 *
 * Never throws. Reuses the tolerant display parser so directive parsing stays
 * in one place; the bindable asset kinds (bg / char / cg / music / sfx)
 * contribute here.
 */
export function parseSceneSymbols(source: string): ParsedSymbols {
  const backgrounds: string[] = [];
  const cg: string[] = [];
  const music: string[] = [];
  const sfx: string[] = [];
  // Preserve first-seen character order while accumulating looks per id.
  const charOrder: string[] = [];
  const charLooks = new Map<string, string[]>();

  const seenBg = new Set<string>();
  const seenCg = new Set<string>();
  const seenMusic = new Set<string>();
  const seenSfx = new Set<string>();

  const registerChar = (id: string): string[] => {
    let looks = charLooks.get(id);
    if (!looks) {
      looks = [];
      charLooks.set(id, looks);
      charOrder.push(id);
    }
    return looks;
  };
  const addLook = (id: string, look: string | undefined) => {
    const looks = registerChar(id);
    if (look && !looks.includes(look)) looks.push(look);
  };

  for (const block of parseLsBlocks(source)) {
    switch (block.kind) {
      case "bg": {
        const name = block.name;
        if (name && !seenBg.has(name)) {
          seenBg.add(name);
          backgrounds.push(name);
        }
        break;
      }
      case "cg": {
        const name = block.name;
        if (name && !seenCg.has(name)) {
          seenCg.add(name);
          cg.push(name);
        }
        break;
      }
      case "music": {
        // `@music <track>` / `&music <track>` names an asset; `@music stop`
        // (block.stop) only controls playback and names nothing bindable.
        const name = block.stop ? undefined : block.name;
        if (name && !seenMusic.has(name)) {
          seenMusic.add(name);
          music.push(name);
        }
        break;
      }
      case "sfx": {
        const name = block.name;
        if (name && !seenSfx.has(name)) {
          seenSfx.add(name);
          sfx.push(name);
        }
        break;
      }
      case "char": {
        // `@<char> <pose>` registers a look; `@<char> bubble <type>` only
        // registers the character (bubble is reserved, never a pose asset).
        const id = block.character;
        if (!id) break;
        addLook(id, block.bubble ? undefined : block.pose);
        break;
      }
      case "dialogue": {
        // Inline pose-sugar `CHAR [look]:` overrides the current pose → a look.
        // The speaker is SCREAMING; the canonical char id is lowercase.
        if (block.pose) addLook(block.speaker.toLowerCase(), block.pose);
        break;
      }
      default:
        break;
    }
  }

  const characters: ParsedCharacter[] = charOrder.map((id) => ({
    id,
    looks: charLooks.get(id) ?? [],
  }));

  return { backgrounds, characters, cg, music, sfx };
}
